import type { Ton } from '../commun.js'
import { estVueDomaine, objet, type AppareilBref, type Collecteur, type EvenementSonde, type Generique, type SalleDomaine, type Seuil, type SourceDomaine } from '../video/kit-modele.js'

/**
 * BÂTIMENT — modèle pur de `GET /v1/power`, du service Home Assistant
 * (`GET /v1/services/home-assistant`) et des détecteurs de fumée
 * (`GET /v1/devices?family=fire_safety`, événements SMOKE_*), 21/10/2026.
 *
 * Règles : les détecteurs de fumée passent EN TÊTE ; une coupure secteur est
 * UN SEUL incident (celui de Sonde), avec l'autonomie restante ; une mesure
 * absente s'écrit « — » (un 0 n'est affiché que s'il a été mesuré).
 */

export interface Onduleur {
  device: AppareilBref
  on_battery: boolean | null
  low_battery: boolean | null
  charge_pct: number | null
  runtime_min: number | null
  load_pct: number | null
  power_w: number | null
  input_v: number | null
  output_v: number | null
  battery_v: number | null
  level: 'ok' | 'warning' | 'critical' | null
  at: string | null
  data_age_s: number | null
}

export interface BudgetPoe {
  device: AppareilBref
  budget_w: number | null
  used_w: number | null
  pct: number | null
  level: 'ok' | 'warning' | 'critical' | null
  at: string | null
  data_age_s: number | null
}

export interface CoupureSecteur { active: boolean; since: string | null; incident_id: string | null; devices: number | null }

export interface PowerDoc {
  generated_at: string
  collector: Collecteur
  sources: SourceDomaine[]
  thresholds: Record<string, Seuil>
  ups: Onduleur[]
  poe: BudgetPoe[]
  zones: SalleDomaine[]
  mains_outage: CoupureSecteur
  generic: Generique[]
}

export function estPowerDoc(x: unknown): x is PowerDoc {
  return estVueDomaine(x, ['ups', 'poe', 'zones', 'generic']) && objet(x['mains_outage'])
}

export interface ServiceSonde {
  key: string
  label: string
  state: string
  state_label: string
  headline: string | null
  problems: string[]
  metrics: { key: string; label: string; value: number | string | null; unit: string | null }[]
  sources: SourceDomaine[]
  showing_lkg?: boolean
  data_age_s: number | null
}

export interface ServiceDoc { generated_at: string; collector: Collecteur; service: ServiceSonde }

export function estServiceDoc(x: unknown): x is ServiceDoc {
  if (!objet(x) || !objet(x['service'])) return false
  const s = x['service']
  return typeof s['state'] === 'string' && Array.isArray(s['metrics']) && Array.isArray(s['problems'])
}

/* ═══════════════ Détecteurs de fumée ═══════════════ */

/** Les alarmes en cours : pour chaque détecteur, son DERNIER événement fumée est « détectée ». */
export function alarmesFumee(events: EvenementSonde[]): EvenementSonde[] {
  const dernier = new Map<string, EvenementSonde>()
  for (const e of events) {
    if (e.kind !== 'SMOKE_DETECTED' && e.kind !== 'SMOKE_CLEARED') continue
    const k = e.uid ?? e.entity_key ?? String(e.id)
    const d = dernier.get(k)
    if (!d || e.at > d.at) dernier.set(k, e)
  }
  return [...dernier.values()].filter((e) => e.kind === 'SMOKE_DETECTED').sort((a, z) => z.at.localeCompare(a.at))
}

/** Détecteurs rangés : hors ligne d'abord, puis inconnus, puis en ligne. */
export function detecteursRanges(d: AppareilBref[]): AppareilBref[] {
  const rang: Record<string, number> = { OFFLINE: 0, DEGRADED: 1, UNAVAILABLE: 2, STALE: 2, UNKNOWN: 2, MAINTENANCE: 3, OFF_EXPECTED: 4, ONLINE: 5 }
  return [...d].sort((a, z) => (rang[a.state.display_state] ?? 2) - (rang[z.state.display_state] ?? 2) || a.display_name.localeCompare(z.display_name, 'fr', { numeric: true }))
}

/* ═══════════════ Onduleur ═══════════════ */

export function etatOnduleur(u: Onduleur): { ton: Ton; libelle: string } {
  if (u.device.state.greyed) return { ton: 'gris', libelle: 'Donnée périmée' }
  if (u.on_battery === true) return { ton: 'err', libelle: u.low_battery ? 'Sur batterie, batterie faible' : 'Sur batterie' }
  if (u.on_battery === false) return u.level === 'warning' || u.level === 'critical' ? { ton: 'warn', libelle: 'Sur secteur, à surveiller' } : { ton: 'ok', libelle: 'Sur secteur' }
  return { ton: 'gris', libelle: 'Alimentation inconnue' }
}

/**
 * Fin d'autonomie estimée : autonomie lue moins l'âge de la lecture. null si
 * l'onduleur n'est pas sur batterie ou si l'autonomie n'est pas lue.
 */
export function finAutonomie(u: Onduleur, maintenant: number): { reste_min: number; fin: Date } | null {
  if (u.on_battery !== true || u.runtime_min === null) return null
  const reste = u.runtime_min - (u.data_age_s ?? 0) / 60
  return { reste_min: Math.max(0, Math.round(reste * 10) / 10), fin: new Date(maintenant + Math.max(0, reste) * 60_000) }
}

/* ═══════════════ Home Assistant ═══════════════ */

export interface SanteHa { en_marche: boolean | null; api_ms: number | null; entites: number | null; suivies: number | null; indisponibles: number | null }

export function santeHa(s: ServiceSonde): SanteHa {
  const v = (suffixe: string): number | null => {
    const m = s.metrics.find((x) => x.key.endsWith(`.${suffixe}`) || x.key === suffixe)
    return typeof m?.value === 'number' ? m.value : null
  }
  const run = v('batiment.ha_running')
  return {
    en_marche: run === 1 ? true : run === 0 ? false : null,
    api_ms: v('batiment.ha_api_ms'), entites: v('batiment.ha_entities'), suivies: v('batiment.ha_tracked'), indisponibles: v('batiment.ha_unavailable'),
  }
}

/* ═══════════════ Énergie ═══════════════ */

export interface Compteur { cle: string; nom: string; mesure: 'puissance' | 'energie'; valeur: number | null; unite: string | null; age_s: number | null }

/** « ha:sensor.compteur_tarif_b_puissance » → « Compteur tarif b puissance ». */
export function nomCompteur(entity: string): string {
  const brut = entity.replace(/^ha:/, '').replace(/^[a-z_]+\./, '').replace(/_/g, ' ').trim()
  return brut ? brut.charAt(0).toUpperCase() + brut.slice(1) : entity
}

/** Compteurs d'énergie lus dans Home Assistant (puissance en W, énergie en kWh). */
export function compteurs(generic: Generique[]): Compteur[] {
  return generic
    .filter((g) => g.metric === 'batiment.power_w' || g.metric.startsWith('batiment.energy_'))
    .map((g): Compteur => ({
      cle: `${g.entity_key}:${g.metric}`, nom: nomCompteur(g.entity_key), mesure: g.metric === 'batiment.power_w' ? 'puissance' : 'energie',
      valeur: g.value, unite: g.unit, age_s: g.data_age_s,
    }))
    .sort((a, z) => a.nom.localeCompare(z.nom, 'fr', { numeric: true }))
}

/** Mesures d'un onduleur pas encore au registre (entités `ups:*`). */
export function mesuresOnduleur(generic: Generique[]): Generique[] {
  return generic.filter((g) => g.entity_key.startsWith('ups:'))
}

/* ═══════════════ Portes (UniFi Access) ═══════════════ */

/** Au-delà de 15 min, une mesure de porte est périmée : grise, jamais « fermée » ni « verrouillée ». */
export const PORTE_PERIMEE_S = 900

export interface PorteAccess {
  cle: string
  /** Identifiant UniFi Access de la porte (affiché seulement avec le détail technique). */
  id: string
  /** Nom de la porte dans UniFi Access (« ACC-001-STAFF »), `null` si Sonde ne le publie pas. */
  nom: string | null
  /** `null` : non remonté — « — », jamais « ouverte » ni « déverrouillée ». */
  verrouillee: boolean | null
  ouverte: boolean | null
  age_s: number | null
  perime: boolean
}

export interface AccesUnifi {
  en_ligne: number | null
  total: number | null
  introuvables: number | null
  age_s: number | null
  perime: boolean
  portes: PorteAccess[]
}

const bool01 = (v: number | null | undefined): boolean | null => (v === 1 ? true : v === 0 ? false : null)
const plusVieux = (ages: (number | null | undefined)[]): number | null => {
  const a = ages.filter((x): x is number => typeof x === 'number' && Number.isFinite(x))
  return a.length ? Math.max(...a) : null
}

/**
 * Ce que Sonde publie d'UniFi Access dans la vue vidéo (`generic`) :
 * `source:unifi_access` (appareils en ligne / connus, postes du registre
 * introuvables) et une entrée `access_door:<id>` par porte (gâche
 * verrouillée, capteur de position). `null` = rien de lu.
 */
export function accesUnifi(generic: readonly Generique[]): AccesUnifi | null {
  const src = generic.filter((g) => g.entity_key === 'source:unifi_access' && g.metric.startsWith('protect.access_'))
  const v = (m: string) => src.find((g) => g.metric === m) ?? null
  const parPorte = new Map<string, Generique[]>()
  for (const g of generic) {
    if (!g.entity_key.startsWith('access_door:')) continue
    parPorte.set(g.entity_key, [...(parPorte.get(g.entity_key) ?? []), g])
  }
  if (src.length === 0 && parPorte.size === 0) return null
  const portes = [...parPorte.entries()]
    .map(([cle, gs]): PorteAccess => {
      const age = plusVieux(gs.map((g) => g.data_age_s))
      const nom = gs.map((g) => g.entity_label).find((l): l is string => typeof l === 'string' && l.trim() !== '')?.trim() ?? null
      return {
        cle, id: cle.slice('access_door:'.length), nom,
        verrouillee: bool01(gs.find((g) => g.metric === 'protect.door_locked')?.value),
        ouverte: bool01(gs.find((g) => g.metric === 'protect.door_open')?.value),
        age_s: age, perime: age === null || age > PORTE_PERIMEE_S,
      }
    })
    // Les portes nommées d'abord, par nom ; les autres par identifiant.
    .sort((a, z) => (a.nom === null ? 1 : 0) - (z.nom === null ? 1 : 0) || (a.nom ?? a.cle).localeCompare(z.nom ?? z.cle, 'fr'))
  const age = plusVieux(src.map((g) => g.data_age_s))
  return {
    en_ligne: v('protect.access_devices_online')?.value ?? null,
    total: v('protect.access_devices_total')?.value ?? null,
    introuvables: v('protect.access_registry_unmatched')?.value ?? null,
    age_s: age, perime: age === null || age > PORTE_PERIMEE_S,
    portes,
  }
}

/** Couleur des appareils Access : vert si tous répondent, ambre sinon ; inconnu ou périmé = gris. */
export function tonAcces(a: Pick<AccesUnifi, 'en_ligne' | 'total' | 'perime'>): Ton {
  if (a.perime || a.en_ligne === null || a.total === null || a.total === 0) return 'gris'
  return a.en_ligne >= a.total ? 'ok' : 'warn'
}

/** La vue vidéo, réduite à ses mesures déclarées (`generic`) : c'est là que Sonde publie UniFi Access. */
export function estVueGenerique(x: unknown): x is { generated_at: string; generic: Generique[] } {
  return objet(x) && Array.isArray(x['generic'])
    && (x['generic'] as unknown[]).every((g) => objet(g) && typeof g['entity_key'] === 'string' && typeof g['metric'] === 'string')
}
